import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { doc, getDoc, query, where, collection, getDocs } from 'firebase/firestore';
import { db } from '@/lib/firebase/config';
import { createUser } from '@/lib/firebase/auth';
import { addMemberToOrganization, getOrganizationManagers, isOrganizationMember } from '@/lib/firebase/queries/organizations';
import { addGroupMember, isGroupMember } from '@/lib/firebase/queries/group-members';
import { isVirtualMember } from '@/lib/firebase/queries/virtual-membership';
import { getGroupHierarchy } from '@/lib/firebase/queries/hierarchical-groups';
import { generateUniqueEmail } from '@/lib/utils/email';
import { reservePhone, releasePhone } from '@/lib/firebase/queries/phone-management';
import { toast } from 'sonner';
import { Loader2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useLanguage } from '@/contexts/LanguageContext';
import { PublicCustomerDialogFormWithOTP } from '@/components/manager/PublicCustomerDialogFormWithOTP';
import { isPhoneVerified, cleanupOTP } from '@/lib/services/otp-verification';
import type { CustomerFormData } from '@/components/manager/types';
import type { Organization } from '@/types/organization';
import type { Group } from '@/types/support';

// Clean up the phone reservation if user creation fails
async function cleanupPhoneReservation(phone: string) {
  try {
    await releasePhone(phone, ''); // Empty userId since we're cleaning up
  } catch (error) {
    console.error('Error cleaning up phone reservation:', error);
  }
}

// Check if user already exists by phone number
async function checkExistingUser(phone: string) {
  const usersQuery = query(
    collection(db, 'users'),
    where('phone', '==', phone)
  );
  const userSnapshot = await getDocs(usersQuery);
  return userSnapshot.empty ? null : { id: userSnapshot.docs[0].id, ...userSnapshot.docs[0].data() };
}

export function PublicUserRegistrationPage() {
  const { organizationId } = useParams<{ organizationId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t, dir } = useLanguage();
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Get URL parameters for section and group
  const selectedSectionId = searchParams.get('section');
  const selectedGroupId = searchParams.get('group');

  useEffect(() => {
    if (organizationId) {
      loadOrganization();
      loadGroups();
    }
  }, [organizationId]);

  // Check if current user is already a member and handle group assignment
  useEffect(() => {
    const checkExistingMember = async () => {
      if (!organization || !selectedGroupId) return;

      try {
        // Get current user from auth context
        const { getAuth, onAuthStateChanged } = await import('firebase/auth');
        const auth = getAuth();
        
        onAuthStateChanged(auth, async (user) => {
          if (user) {
            // Check if user is already a member of this organization
            const isMember = await isOrganizationMember(organization.id, user.uid);
            
            if (isMember) {
              // User is already a member, try to add them to the selected group
              console.log('User is already a member, adding to group:', selectedGroupId);
              await addUserToGroup(selectedGroupId, user.uid);
              
              // Redirect to login/dashboard after a short delay
              setTimeout(() => {
                navigate('/login');
              }, 2000);
            }
          }
        });
      } catch (error) {
        console.error('Error checking existing member:', error);
      }
    };

    checkExistingMember();
  }, [organization, selectedGroupId, navigate, t]);

  const loadOrganization = async () => {
    if (!organizationId) return;

    try {
      setLoading(true);
      const orgRef = doc(db, 'organizations', organizationId);
      const orgDoc = await getDoc(orgRef);

      if (!orgDoc.exists()) {
        setError('Organization not found');
        return;
      }

      setOrganization({
        id: orgDoc.id,
        ...orgDoc.data()
      } as Organization);
    } catch (error) {
      console.error('Error loading organization:', error);
      setError('Failed to load organization');
    } finally {
      setLoading(false);
    }
  };

  const loadGroups = async () => {
    if (!organizationId) return;

    try {
      const hierarchy = await getGroupHierarchy(organizationId);
      // Flatten the hierarchy to get all groups
      const flatGroups = flattenHierarchy(hierarchy);
      setGroups(flatGroups);
    } catch (error) {
      console.error('Error loading groups:', error);
      // Don't show error, groups are optional
    }
  };

  const flattenHierarchy = (nodes: (Group & { children: Group[] })[]): Group[] => {
    const result: Group[] = [];
    for (const node of nodes) {
      result.push(node);
      result.push(...flattenHierarchy(node.children));
    }
    return result;
  };

  // Function to add user to group with proper error handling
  const addUserToGroup = async (groupId: string, userId: string) => {
    try {
      // Check if user is already a virtual member of the group/section
      const isAlreadyMember = await isVirtualMember(groupId, userId);
      if (isAlreadyMember) {
        toast.info(t('auth.alreadyInGroup') || 'You are already a member of this group');
        return true;
      }

      // Get organization managers to use one as the addedByUserId
      const managers = await getOrganizationManagers(organization!.id);
      const addedByUserId = managers.length > 0 ? managers[0].id : null;
      
      if (addedByUserId) {
        await addGroupMember(groupId, userId, addedByUserId);
        toast.success(t('publicRegistration.groupAdded') || 'Successfully added to group!');
        return true;
      } else {
        console.warn('No organization managers found, skipping group assignment');
        toast.warning(t('publicRegistration.groupError') || 'Added to organization but failed to add to group (no managers found)');
        return false;
      }
    } catch (groupError) {
      console.error('Error adding to group:', groupError);
      toast.warning(t('publicRegistration.groupError') || 'Added to organization but failed to add to group');
      return false;
    }
  };

  const handleExistingUserLogin = async (userId: string) => {
    if (!organization) return;

    try {
      // Check if user is already a member of this organization
      const alreadyMember = await isOrganizationMember(organization.id, userId);
      if (!alreadyMember) {
        // Add to organization
        await addMemberToOrganization(
          organization.id,
          userId,
          'customer',
          'self-registration-existing'
        );
        
        // Add to selected group if specified
        const groupToAdd = selectedGroupId;
        if (groupToAdd && groups.length > 0) {
          await addUserToGroup(groupToAdd, userId);
        }
        
        toast.success(t('publicRegistration.existingUser') || 'Welcome back! You have been added to the organization.');
      } else {
        // Already a member, just add to group if needed
        const groupToAdd = selectedGroupId;
        if (groupToAdd && groups.length > 0) {
          await addUserToGroup(groupToAdd, userId);
        }
        
        toast.info(t('publicRegistration.existingUser') || 'You are already a member of this organization.');
      }
    } catch (error) {
      console.error('Error handling existing user login:', error);
      toast.error('Failed to add user to organization');
      throw error;
    }
  };

  const handleSubmit = async (values: CustomerFormData & { selectedSectionId?: string; selectedGroupId?: string }) => {
    if (!organization) return;

    try {
      setIsSubmitting(true);

      // Verify that phone number has been verified via OTP
      const phoneVerified = await isPhoneVerified(values.phone);
      if (!phoneVerified) {
        toast.error(t('otp.errors.phoneNotVerified') || 'Phone number must be verified before registration');
        setIsSubmitting(false);
        return;
      }

      // Check if user already exists
      const existingUser = await checkExistingUser(values.phone);
      
      let userId: string;
      let isNewUser = false;

      if (existingUser) {
        // User already exists, just use their ID
        userId = existingUser.id;
        // If already a member of this org, do NOT add again and do NOT reset logs
        const alreadyMember = await isOrganizationMember(organization.id, userId);
        if (alreadyMember) {
          toast.info(t('publicRegistration.existingUser') || 'You are already a member of this organization.');
          // Prevent resetting logs: skip copying default habits
          // Optionally ensure no reset by doing nothing
          // Add to selected group if specified (optional convenience)
          const groupToAdd = values.selectedGroupId || selectedGroupId;
          if (groupToAdd && groups.length > 0) {
            await addUserToGroup(groupToAdd, userId);
          }
          navigate('/login');
          return;
        }
        toast.info(t('publicRegistration.existingUser') || 'Welcome back! Adding you to the organization.');
      } else {
        // New user - create account
        isNewUser = true;
        
        // Generate unique email based on last name
        const email = await generateUniqueEmail(values.lastNameEn);

        // Create user account using phone number as password
        const { user } = await createUser({
          ...values,
          email,
          password: values.phone, // Use phone number as password
          phone: values.phone,    // Use the actual phone from form
          role: 'customer'
        });

        userId = user.uid;

        // Reserve the phone number with the actual user ID
        await reservePhone(values.phone, user.uid);
        
        toast.success(t('publicRegistration.success') || 'Registration successful!');
        toast.info(t('publicRegistration.emailInfo') || `Generated email: ${email}`);
        toast.info(t('publicRegistration.passwordInfo') || `Password: ${values.phone}`);
        toast.info(t('publicRegistration.phoneLoginInfo') || `You can also login using your phone number (${values.phone}) as both username and password`);
      }

      // Add to organization (only when not already a member)
      await addMemberToOrganization(
        organization.id,
        userId,
        'customer',
        isNewUser ? 'self-registration' : 'self-registration-existing'
      );

      // Note: Organization default habits system has been removed
      // Users will create their own personal habits

      // Add to selected group if specified (from form or URL parameters)
      const groupToAdd = values.selectedGroupId || selectedGroupId;
      if (groupToAdd && groups.length > 0) {
        await addUserToGroup(groupToAdd, userId);
      }
      
      // Clean up OTP verification record
      await cleanupOTP(values.phone);

      // Redirect to login page
      navigate('/login');
    } catch (error: any) {
      // Clean up phone reservation if user creation fails and it's a new user
      if (values.phone && !await checkExistingUser(values.phone)) {
        await cleanupPhoneReservation(values.phone);
      }
      console.error('Error registering user:', error);
      toast.error(error.message || t('publicRegistration.error') || 'Failed to register');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary/30 px-4">
        <div className="text-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin mx-auto" />
          <p className="text-muted-foreground text-sm">
            {t('publicRegistration.loading') || 'Loading organization...'}
          </p>
        </div>
      </div>
    );
  }

  if (error || !organization) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary/30 px-4">
        <Card className="w-full max-w-md mx-auto">
          <CardContent className="pt-6 px-4 sm:px-6">
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="text-sm">
                {error || t('publicRegistration.organizationNotFound') || 'Organization not found'}
              </AlertDescription>
            </Alert>
            <div className="mt-4 flex justify-center">
              <Button onClick={() => navigate('/login')} className="w-full sm:w-auto">
                {t('publicRegistration.backToLogin') || 'Back to Login'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Check if registration is paused
  if (organization?.registrationPaused) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-secondary/30 p-4 sm:p-6">
        {/* Logos */}
        <div className="mb-6 sm:mb-8 flex items-center gap-6 sm:gap-8">
          {/* Namat Logo */}
          <img 
            src="/NamatLogo.png" 
            alt="Namat Platform Logo" 
            className="h-24 sm:h-30 md:h-36 w-auto object-contain"
          />
          
          {/* Organization Logo */}
          {organization?.logoUrl && (
            <>
              <div className="h-16 sm:h-20 md:h-24 w-px bg-border"></div>
              <img 
                src={organization.logoUrl} 
                alt={organization.name} 
                className="h-24 sm:h-30 md:h-36 w-auto object-contain"
              />
            </>
          )}
        </div>
        
        {/* Paused Message Card */}
        <Card className="w-full max-w-2xl mx-auto shadow-lg">
          <CardHeader className="px-4 sm:px-6 pt-6 pb-4 text-center">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-amber-100 dark:bg-amber-900/20 rounded-full flex items-center justify-center">
                <Loader2 className="h-8 w-8 text-amber-600 animate-spin" />
              </div>
            </div>
            <CardTitle className="text-xl sm:text-2xl font-semibold text-amber-700 dark:text-amber-300">
              {t('publicRegistration.paused.title') || 'التسجيل متوقف مؤقتاً'}
            </CardTitle>
            <p className="text-center text-muted-foreground text-sm sm:text-base mt-2 font-medium">
              {organization.name}
            </p>
          </CardHeader>
          <CardContent className="px-4 sm:px-6 pb-6 text-center space-y-4">
            <div className="space-y-2">
              <p className="text-muted-foreground">
                {t('publicRegistration.paused.message') || 
                 'نعتذر، التسجيل في هذه المنظمة متوقف مؤقتاً. يرجى المحاولة مرة أخرى لاحقاً.'}
              </p>
              <p className="text-sm text-muted-foreground">
                {t('publicRegistration.paused.contact') || 
                 'إذا كنت تحتاج إلى مساعدة عاجلة، يرجى التواصل مع إدارة المنظمة مباشرة.'}
              </p>
            </div>
            
            <div className="pt-4">
              <Button 
                variant="outline" 
                onClick={() => window.location.reload()}
                className="gap-2"
              >
                <Loader2 className="h-4 w-4" />
                {t('publicRegistration.paused.refresh') || 'تحديث الصفحة'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-secondary/30 p-4 sm:p-6">
      {/* Logos */}
      <div className="mb-6 sm:mb-8 flex items-center gap-6 sm:gap-8">
        {/* Namat Logo */}
        <img 
          src="/NamatLogo.png" 
          alt="Namat Platform Logo" 
          className="h-24 sm:h-30 md:h-36 w-auto object-contain"
        />
        
        {/* Organization Logo */}
        {organization?.logoUrl && (
          <>
            <div className="h-16 sm:h-20 md:h-24 w-px bg-border"></div>
            <img 
              src={organization.logoUrl} 
              alt={organization.name} 
              className="h-24 sm:h-30 md:h-36 w-auto object-contain"
            />
          </>
        )}
      </div>
      
      {/* Form Card */}
      <Card className="w-full max-w-2xl mx-auto shadow-lg">
        <CardHeader className="px-4 sm:px-6 pt-6 pb-4">
          <CardTitle className="text-center text-xl sm:text-2xl font-semibold">
            {t('publicRegistration.createAccount') || 'Create Your Account'}
          </CardTitle>
          <p className="text-center text-muted-foreground text-sm sm:text-base mt-2 font-medium">
            {organization.name}
          </p>
        </CardHeader>
        <CardContent className="px-4 sm:px-6 pb-6">
          <PublicCustomerDialogFormWithOTP
            onSubmit={handleSubmit}
            isLoading={isSubmitting}
            title={t('publicRegistration.createAccount') || 'Create Your Account'}
            groups={groups}
            showGroupSelection={groups.some(group => group.groupType !== 'parent')}
            defaultSectionId={selectedSectionId || undefined}
            defaultGroupId={selectedGroupId || undefined}
            embedded={true}
            organizationId={organization.id}
            onExistingUserLogin={handleExistingUserLogin}
          />
        </CardContent>
      </Card>
    </div>
  );
} 